import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  TextField,
} from "@material-ui/core";
import { useEffect, useState } from "react";
import { tokens } from "../app/theme";
import { isScheduleExpression, parseList } from "./pluginValidation";
import {
  useApplicationConfig,
  usePersistApplicationConfig,
} from "./useApplicationConfig";

interface HarvestFormErrors {
  harvestToTeamsSchedule?: string;
  cacheExpirationInSeconds?: string;
}

const MIN_CACHE_TTL = 60;
const MAX_CACHE_TTL = 86400;

export function HarvestPluginForm() {
  const config = useApplicationConfig();
  const persist = usePersistApplicationConfig();
  const harvest = config.plugins.harvest;

  const [enabled, setEnabled] = useState(harvest.enabled);
  const [schedule, setSchedule] = useState(harvest.harvestToTeamsSchedule);
  const [cacheTtl, setCacheTtl] = useState(
    String(harvest.cacheExpirationInSeconds)
  );
  const [opsTaskIds, setOpsTaskIds] = useState(harvest.opsTaskIds.join("\n"));
  const [errors, setErrors] = useState<HarvestFormErrors>({});

  useEffect(() => {
    setEnabled(harvest.enabled);
    setSchedule(harvest.harvestToTeamsSchedule);
    setCacheTtl(String(harvest.cacheExpirationInSeconds));
    setOpsTaskIds(harvest.opsTaskIds.join("\n"));
  }, [harvest]);

  function validate(): HarvestFormErrors {
    const next: HarvestFormErrors = {};
    if (enabled && !isScheduleExpression(schedule)) {
      next.harvestToTeamsSchedule =
        "Must be an AWS cron(...) or rate(...) expression";
    }
    const ttl = Number(cacheTtl.trim());
    if (
      !/^\d+$/.test(cacheTtl.trim()) ||
      !Number.isInteger(ttl) ||
      ttl < MIN_CACHE_TTL ||
      ttl > MAX_CACHE_TTL
    ) {
      next.cacheExpirationInSeconds = `Must be an integer from ${MIN_CACHE_TTL} to ${MAX_CACHE_TTL}`;
    }
    return next;
  }

  function save(): void {
    const validation = validate();
    setErrors(validation);
    if (Object.keys(validation).length > 0) {
      return;
    }
    persist({
      ...config,
      plugins: {
        ...config.plugins,
        harvest: {
          ...harvest,
          enabled,
          harvestToTeamsSchedule: schedule.trim(),
          cacheExpirationInSeconds: Number(cacheTtl.trim()),
          opsTaskIds: parseList(opsTaskIds),
        },
      },
    }).catch(() => {
      /* error already surfaced via app feedback */
    });
  }

  return (
    <Box display="flex" flexDirection="column" gridGap={16} marginTop={2}>
      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
            inputProps={{ "data-testid": "harvest-enabled" } as any}
          />
        }
        label="Enabled"
      />
      <TextField
        label="Harvest-to-Teams schedule"
        value={schedule}
        onChange={(event) => setSchedule(event.target.value)}
        error={!!errors.harvestToTeamsSchedule}
        helperText={
          errors.harvestToTeamsSchedule ||
          "AWS cron(...) or rate(...) expression"
        }
        variant="outlined"
        inputProps={{ "data-testid": "harvest-schedule" }}
      />
      <TextField
        label="Cache TTL (seconds)"
        value={cacheTtl}
        onChange={(event) => setCacheTtl(event.target.value)}
        error={!!errors.cacheExpirationInSeconds}
        helperText={
          errors.cacheExpirationInSeconds ||
          `Integer seconds, ${MIN_CACHE_TTL}–${MAX_CACHE_TTL}.`
        }
        variant="outlined"
        inputProps={{ "data-testid": "harvest-cache-ttl", inputMode: "numeric" }}
      />
      <TextField
        label="Ops task IDs"
        value={opsTaskIds}
        onChange={(event) => setOpsTaskIds(event.target.value)}
        multiline
        minRows={2}
        variant="outlined"
        helperText="One Harvest task ID per line."
      />
      <Box>
        <Button
          variant="contained"
          color="primary"
          onClick={save}
          data-testid="harvest-save"
        >
          Save Harvest settings
        </Button>
      </Box>
      <Box style={{ color: tokens.fg3, fontSize: 12 }}>
        Harvest credentials stay in secure storage and are never edited here.
      </Box>
    </Box>
  );
}
